/**
 * Global Variable Manager Component
 * 
 * Manage company-wide and system-wide global variables.
 * Forward-compatible with upcoming backend endpoints.
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Tag,
  Popconfirm,
  message,
  Spin,
  Empty
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

interface GlobalVariable {
  id: string;
  company_id: string;
  variable_key: string;
  variable_type: string;
  default_value?: string;
  description?: string;
  scope: 'company' | 'system';
  validation_rules?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
}

interface GlobalVariableManagerProps {
  companyId: string;
}

export const GlobalVariableManager: React.FC<GlobalVariableManagerProps> = ({
  companyId
}) => {
  const [variables, setVariables] = useState<GlobalVariable[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingVariable, setEditingVariable] = useState<GlobalVariable | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadVariables();
  }, [companyId]);

  const loadVariables = async () => {
    setLoading(true);
    try {
      // Note: Global variables endpoint doesn't exist - using global properties instead
      // This component may need to be refactored to use global properties endpoints
      // For now, return empty array to avoid 404 errors
      console.log('Global variables feature not available - use global properties instead');
      setVariables([]);
    } catch (error: any) {
      console.error('Error loading global variables:', error);
      message.error('Failed to load global variables');
      setVariables([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddVariable = () => {
    setEditingVariable(null);
    setShowModal(true);
  };

  const handleEditVariable = (variable: GlobalVariable) => {
    setEditingVariable(variable);
    setShowModal(true);
  };

  // Initialize form when modal opens (after Form element is mounted)
  useEffect(() => {
    if (showModal) {
      if (editingVariable) {
        form.setFieldsValue(editingVariable);
      } else {
        form.resetFields();
        form.setFieldsValue({ scope: 'company' });
      }
    }
  }, [showModal, editingVariable, form]);

  const handleDeleteVariable = async (variableId: string) => {
    try {
      // Note: Global variables endpoint doesn't exist - use global properties instead
      message.warning('Global variables feature not available - use global properties instead');
      // await apiClient.delete(`/companies/${companyId}/global-variables/${variableId}`);
      // loadVariables();
    } catch (error: any) {
      console.error('Error deleting global variable:', error);
      message.error('Failed to delete global variable');
    }
  };

  const handleSaveVariable = async () => {
    try {
      // Note: Global variables endpoint doesn't exist - use global properties instead
      message.warning('Global variables feature not available - use global properties instead');
      // const values = await form.validateFields();
      // if (editingVariable) {
      //   await apiClient.put(`/companies/${companyId}/global-variables/${editingVariable.id}`, {
      //     ...values,
      //     company_id: companyId
      //   });
      //   message.success('Global variable updated');
      // } else {
      //   await apiClient.post(`/companies/${companyId}/global-variables`, {
      //     ...values,
      //     company_id: companyId
      //   });
      //   message.success('Global variable created');
      // }
      setShowModal(false);
      loadVariables();
    } catch (error: any) {
      if (error.response?.status === 404) {
        message.warning('Global variables feature not yet available on the backend');
      } else {
        console.error('Error saving global variable:', error);
        message.error('Failed to save global variable');
      }
    }
  };

  const columns = [
    {
      title: 'Variable Key',
      dataIndex: 'variable_key',
      key: 'variable_key',
      sorter: (a: GlobalVariable, b: GlobalVariable) =>
        a.variable_key.localeCompare(b.variable_key)
    },
    {
      title: 'Type',
      dataIndex: 'variable_type',
      key: 'variable_type',
      sorter: (a: GlobalVariable, b: GlobalVariable) =>
        a.variable_type.localeCompare(b.variable_type)
    },
    {
      title: 'Scope',
      dataIndex: 'scope',
      key: 'scope',
      render: (scope: string) => (
        <Tag color={scope === 'system' ? 'red' : 'blue'}>{scope}</Tag>
      )
    },
    {
      title: 'Default Value',
      dataIndex: 'default_value',
      key: 'default_value',
      ellipsis: true
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: GlobalVariable) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditVariable(record)}
            size="small"
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this global variable?"
            onConfirm={() => handleDeleteVariable(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card
      title={
        <Title level={4} style={{ margin: 0 }}>
          Global Variables
        </Title>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadVariables} loading={loading}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddVariable}>
            Add Variable
          </Button>
        </Space>
      }
    >
      {variables.length === 0 && !loading ? (
        <Empty
          description={
            <div>
              <p>No global variables defined</p>
              <p style={{ fontSize: '12px', color: '#8c8c8c' }}>
                Global variables feature may not be available yet on the backend.
              </p>
            </div>
          }
        />
      ) : (
        <Table
          columns={columns}
          dataSource={variables}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      )}

      <Modal
        title={editingVariable ? 'Edit Global Variable' : 'Add Global Variable'}
        open={showModal}
        onOk={handleSaveVariable}
        onCancel={() => setShowModal(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="variable_key"
            label="Variable Key"
            rules={[{ required: true, message: 'Variable key is required' }]}
          >
            <Input placeholder="e.g., company_fiscal_year_start" />
          </Form.Item>

          <Form.Item
            name="variable_type"
            label="Variable Type"
            rules={[{ required: true, message: 'Variable type is required' }]}
          >
            <Select placeholder="Select type">
              <Option value="string">String</Option>
              <Option value="number">Number</Option>
              <Option value="boolean">Boolean</Option>
              <Option value="date">Date</Option>
              <Option value="currency">Currency</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="scope"
            label="Scope"
            rules={[{ required: true, message: 'Scope is required' }]}
          >
            <Select>
              <Option value="company">Company</Option>
              <Option value="system">System</Option>
            </Select>
          </Form.Item>

          <Form.Item name="default_value" label="Default Value">
            <Input placeholder="Default value for this variable" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Description of this global variable" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

